import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

const HEARTBEAT_INTERVAL = 60_000; // 60 seconds
const ONLINE_THRESHOLD = 2 * 60_000; // 2 minutes

export const useOnlinePresence = () => {
  const { user } = useAuth();

  // Update last_seen periodically
  useEffect(() => {
    if (!user) return;

    const updatePresence = async () => {
      await supabase
        .from('profiles')
        .update({ last_seen: new Date().toISOString() })
        .eq('id', user.id);
    };

    updatePresence();
    const interval = setInterval(updatePresence, HEARTBEAT_INTERVAL);

    // Update on visibility change
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') updatePresence();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);
};

export const useOnlineStatus = (userIds: string[]) => {
  const [statuses, setStatuses] = useState<Record<string, { online: boolean; lastSeen: string | null }>>({});

  const fetchStatuses = useCallback(async () => {
    if (userIds.length === 0) return;

    const { data } = await supabase
      .from('profiles')
      .select('id, last_seen')
      .in('id', userIds);

    if (!data) return;

    const now = Date.now();
    const result: Record<string, { online: boolean; lastSeen: string | null }> = {};
    for (const profile of data) {
      const lastSeen = profile.last_seen;
      const isOnline = lastSeen ? (now - new Date(lastSeen).getTime()) < ONLINE_THRESHOLD : false;
      result[profile.id] = { online: isOnline, lastSeen };
    }
    setStatuses(result);
  }, [userIds.join(',')]);

  useEffect(() => {
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 30_000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchStatuses]);

  return statuses;
};

export const formatLastSeen = (lastSeen: string | null): string => {
  if (!lastSeen) return 'давно';
  
  const diff = Date.now() - new Date(lastSeen).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;
  if (hours < 24) return `${hours} ч назад`;
  if (days < 7) return `${days} дн назад`;
  return new Date(lastSeen).toLocaleDateString('ru-RU');
};
